import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Email as EmailIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PageSEO from '../components/PageSEO';

const ContactPage: React.FC = () => {
  const { t } = useTranslation();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setShowSuccess(true);
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
      });
    }, 2000);
  };

  const contactInfo = [
    {
      icon: <EmailIcon />,
      title: t('contact.emailSupport'),
      details: ['info@workio.bg', 'support@workio.bg'],
      description: t('contact.emailSupportDesc'),
    },
  ];

  return (
    <Box sx={{ backgroundColor: '#0f0f23', minHeight: '100vh' }}>
      <PageSEO pageKey="contact" />
      <Header />
      
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
        {/* Hero Section */}
        <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
          <Typography
            variant="h2"
            sx={{
              color: '#ffffff',
              fontWeight: 800,
              mb: 3,
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              lineHeight: { xs: 1.2, md: 1.3 },
              background: 'linear-gradient(135deg, #ffffff 0%, #28A0F0 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
{t('contact.title')}
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: '#a0a0a0',
              mb: 4,
              maxWidth: 600,
              mx: 'auto',
              fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
              lineHeight: { xs: 1.4, md: 1.5 },
              px: { xs: 2, sm: 0 },
            }}
          >
{t('contact.subtitle')}
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {/* Contact Form */}
          <Grid item xs={12} lg={8}>
            <Card
              sx={{
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                borderRadius: 4,
                overflow: 'hidden',
              }}
            >
              <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                <Typography
                  variant="h5"
                  sx={{
                    color: '#ffffff',
                    fontWeight: 700,
                    mb: 3,
                    fontSize: { xs: '1.25rem', md: '1.5rem' },
                  }}
                >
  {t('contact.sendMessage')}
                </Typography>
                
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label={t('contact.name')}
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            color: '#ffffff',
                            '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                            '&:hover fieldset': { borderColor: '#28A0F0' },
                            '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                          },
                          '& .MuiInputLabel-root': { color: '#a0a0a0' },
                          '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label={t('contact.email')}
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            color: '#ffffff',
                            '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                            '&:hover fieldset': { borderColor: '#28A0F0' },
                            '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                          },
                          '& .MuiInputLabel-root': { color: '#a0a0a0' },
                          '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label={t('contact.subject')}
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        required
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            color: '#ffffff',
                            '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                            '&:hover fieldset': { borderColor: '#28A0F0' },
                            '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                          },
                          '& .MuiInputLabel-root': { color: '#a0a0a0' },
                          '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label={t('contact.message')}
                        name="message"
                        multiline
                        rows={6}
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            color: '#ffffff',
                            '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                            '&:hover fieldset': { borderColor: '#28A0F0' },
                            '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                          },
                          '& .MuiInputLabel-root': { color: '#a0a0a0' },
                          '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={isSubmitting}
                        startIcon={isSubmitting ? null : <SendIcon />}
                        sx={{
                          background: 'linear-gradient(135deg, #28A0F0 0%, #1d4ed8 100%)',
                          color: '#ffffff',
                          py: 1.5,
                          px: 4,
                          fontSize: '1rem',
                          fontWeight: 600,
                          '&:hover': {
                            background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
                          },
                          '&:disabled': {
                            background: 'rgba(160, 160, 160, 0.3)',
                            color: 'rgba(255, 255, 255, 0.5)',
                          },
                        }}
                      >
{isSubmitting ? t('contact.sending') : t('contact.send')}
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              </CardContent>
            </Card>
          </Grid>

          {/* Contact Information */}
          <Grid item xs={12} lg={4}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {contactInfo.map((info, index) => (
                <Card
                  key={index}
                  sx={{
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    borderRadius: 3,
                    p: 3,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 15px rgba(40, 160, 240, 0.2)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      color: '#28A0F0',
                      mb: 2,
                      '& .MuiSvgIcon-root': {
                        fontSize: { xs: 32, md: 40 },
                      },
                    }}
                  >
                    {info.icon}
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      color: '#ffffff',
                      fontWeight: 600,
                      mb: 1,
                      fontSize: { xs: '1rem', md: '1.25rem' },
                    }}
                  >
                    {info.title}
                  </Typography>
                  {info.details.map((detail, detailIndex) => (
                    <Typography
                      key={detailIndex}
                      variant="body2"
                      sx={{
                        color: '#a0a0a0',
                        mb: 0.5,
                        fontSize: { xs: '0.8rem', md: '0.875rem' },
                      }}
                    >
                      {detail}
                    </Typography>
                  ))}
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#28A0F0',
                      mt: 1,
                      fontSize: { xs: '0.8rem', md: '0.875rem' },
                      fontStyle: 'italic',
                    }}
                  >
                    {info.description}
                  </Typography>
                </Card>
              ))}
            </Box>
          </Grid>
        </Grid>

        {/* FAQ Section */}
        <Box sx={{ mt: { xs: 6, md: 8 } }}>
          <Typography
            variant="h4"
            sx={{
              color: '#ffffff',
              fontWeight: 700,
              mb: 4,
              textAlign: 'center',
              fontSize: { xs: '1.5rem', md: '2rem' },
            }}
          >
{t('contact.faq')}
          </Typography>
          <Grid container spacing={3}>
            {[
              {
                question: t('contact.faq1'),
                answer: 'Обикновено отговаряме в рамките на 24 часа в работните дни.',
              },
              {
                question: t('contact.faq2'),
                answer: t('contact.faq2Answer'),
              },
              {
                question: t('contact.faq3'),
                answer: t('contact.faq3Answer'),
              },
              {
                question: t('contact.faq4'),
                answer: t('contact.faq4Answer'),
              },
            ].map((faq, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card
                  sx={{
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    borderRadius: 3,
                    p: 3,
                    height: '100%',
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      color: '#ffffff',
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { xs: '1rem', md: '1.25rem' },
                    }}
                  >
                    {faq.question}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#a0a0a0',
                      fontSize: { xs: '0.8rem', md: '0.875rem' },
                      lineHeight: 1.5,
                    }}
                  >
                    {faq.answer}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>

      <Footer />

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowSuccess(false)}
          severity="success"
          icon={<CheckCircleIcon />}
          sx={{
            backgroundColor: 'rgba(16, 185, 129, 0.9)',
            color: '#ffffff',
            '& .MuiAlert-icon': {
              color: '#ffffff',
            },
          }}
        >
{t('contact.successMessage')}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContactPage;
